<template>
    <div class="fixed inset-0 z-50 flex" @click.self="$emit('close')">
        <!-- Overlay -->
        <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="$emit('close')"></div>

        <!-- Panel -->
        <div class="relative ml-auto w-full max-w-xl h-full bg-white shadow-2xl flex flex-col overflow-hidden">

            <!-- Header -->
            <div class="flex items-center justify-between px-6 py-5 border-b border-gray-100 flex-shrink-0">
                <div class="flex items-center gap-3 min-w-0">
                    <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center font-black text-orange-600 text-lg flex-shrink-0">
                        {{ prospect.company_name.charAt(0).toUpperCase() }}
                    </div>
                    <div class="min-w-0">
                        <h2 class="font-black text-gray-900 truncate text-base tracking-tight">{{ prospect.company_name }}</h2>
                        <div class="flex items-center gap-2 mt-0.5">
                            <span class="text-[10px] font-bold px-2 py-0.5 rounded-full" :class="getStatusBadge(prospect.status)">
                                {{ statusConfig[prospect.status] ? statusConfig[prospect.status].label : prospect.status }}
                            </span>
                            <span v-if="prospect.effectif" class="text-[10px] text-gray-400 font-medium">{{ prospect.effectif }} employé(s)</span>
                        </div>
                    </div>
                </div>
                <div class="flex items-center gap-2 flex-shrink-0">
                    <button
                        @click="confirmValidate"
                        v-if="!prospect.validated_at"
                        class="flex items-center gap-1.5 px-3 py-2 bg-green-500 text-white text-xs font-bold rounded-xl hover:bg-green-600 transition-all shadow-sm shadow-green-200">
                        <i class="fas fa-check-circle text-[10px]"></i> Valider
                    </button>
                    <span v-else class="flex items-center gap-1.5 px-3 py-2 bg-green-100 text-green-700 text-xs font-bold rounded-xl">
                        <i class="fas fa-check-circle text-[10px]"></i> Client
                    </span>
                    <button @click="$emit('close')" class="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all">
                        <i class="fas fa-times text-sm"></i>
                    </button>
                </div>
            </div>

            <!-- Tabs -->
            <div class="flex border-b border-gray-100 px-6 flex-shrink-0 overflow-x-auto">
                <button
                    v-for="tab in tabs"
                    :key="tab.key"
                    @click="activeTab = tab.key"
                    class="flex items-center gap-1.5 px-3 py-3.5 text-xs font-bold whitespace-nowrap border-b-2 transition-all mr-1"
                    :class="activeTab === tab.key
                        ? 'border-orange-500 text-orange-600'
                        : 'border-transparent text-gray-400 hover:text-gray-700 hover:border-gray-200'">
                    <i :class="[tab.icon, 'text-[10px]']"></i>
                    {{ tab.label }}
                    <span v-if="tab.count !== undefined" class="ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-black"
                        :class="activeTab === tab.key ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-500'">
                        {{ tab.count }}
                    </span>
                </button>
            </div>

            <!-- Body loader -->
            <div v-if="loadingDetail" class="flex-1 flex items-center justify-center">
                <div class="flex flex-col items-center gap-3">
                    <div class="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                    <span class="text-xs text-gray-400">Chargement...</span>
                </div>
            </div>

            <!-- Body -->
            <div v-else class="flex-1 overflow-y-auto">

                <!-- ── INFOS ── -->
                <div v-if="activeTab === 'infos'" class="p-6 space-y-5">
                    <div v-if="!editMode" class="space-y-4">
                        <div class="grid grid-cols-2 gap-4">
                            <div class="bg-gray-50 rounded-xl p-4">
                                <p class="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">Société</p>
                                <p class="text-sm font-bold text-gray-900">{{ detail.company_name }}</p>
                            </div>
                            <div class="bg-gray-50 rounded-xl p-4">
                                <p class="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">Effectif</p>
                                <p class="text-sm font-bold text-gray-900">{{ detail.effectif || '—' }} <span class="text-xs font-normal text-gray-500">employé(s)</span></p>
                            </div>
                            <div class="bg-gray-50 rounded-xl p-4">
                                <p class="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">Statut</p>
                                <span class="text-xs font-bold px-2.5 py-1 rounded-full" :class="getStatusBadge(detail.status)">
                                    {{ statusConfig[detail.status] ? statusConfig[detail.status].label : detail.status }}
                                </span>
                            </div>
                            <div class="bg-gray-50 rounded-xl p-4">
                                <p class="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">Créé le</p>
                                <p class="text-sm font-bold text-gray-900">{{ detail.created_at | momentDate }}</p>
                            </div>
                        </div>
                        <button @click="editMode = true" class="flex items-center gap-2 text-xs font-bold text-orange-500 hover:text-orange-600 transition-colors">
                            <i class="fas fa-pen text-[10px]"></i> Modifier les informations
                        </button>
                    </div>

                    <!-- Edit mode -->
                    <div v-else class="space-y-4">
                        <div>
                            <label class="text-[10px] font-bold text-gray-600 uppercase tracking-wide block mb-1.5">Nom de la société</label>
                            <input v-model="editForm.company_name" type="text" class="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 transition"/>
                        </div>
                        <div>
                            <label class="text-[10px] font-bold text-gray-600 uppercase tracking-wide block mb-1.5">Statut</label>
                            <div class="relative">
                                <select v-model="editForm.status" class="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm appearance-none bg-white focus:outline-none focus:ring-2 focus:ring-orange-400 transition">
                                    <option v-for="(cfg, k) in statusConfig" :key="k" :value="k">{{ cfg.label }}</option>
                                </select>
                                <i class="fas fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none"></i>
                            </div>
                        </div>
                        <div>
                            <label class="text-[10px] font-bold text-gray-600 uppercase tracking-wide block mb-1.5">Effectif</label>
                            <div class="flex gap-2">
                                <button v-for="e in ['1','2-5','5-10','10+']" :key="e" @click="editForm.effectif = e" type="button"
                                    class="flex-1 py-2 rounded-xl text-xs font-bold border transition-all"
                                    :class="editForm.effectif === e ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-500 border-gray-200 hover:border-orange-300'">
                                    {{ e }}
                                </button>
                            </div>
                        </div>
                        <div class="flex gap-2 pt-2">
                            <button @click="saveInfos" :disabled="loadingUpdate" class="flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white text-xs font-bold rounded-xl hover:bg-orange-600 transition-all disabled:opacity-60">
                                <i v-if="loadingUpdate" class="fas fa-spinner fa-spin text-[10px]"></i>
                                <i v-else class="fas fa-check text-[10px]"></i>
                                Enregistrer
                            </button>
                            <button @click="editMode = false" class="px-4 py-2 bg-gray-100 text-gray-600 text-xs font-bold rounded-xl hover:bg-gray-200 transition-all">
                                Annuler
                            </button>
                        </div>
                    </div>
                </div>

                <!-- ── CONTACTS ── -->
                <div v-if="activeTab === 'contacts'" class="p-6 space-y-4">
                    <div v-for="(contact, i) in detail.contacts" :key="contact.id || i" class="bg-gray-50 rounded-xl p-4 border border-gray-100 group relative">
                        <div class="flex items-start justify-between mb-3">
                            <div class="flex items-center gap-2">
                                <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center text-orange-600 font-bold text-xs">
                                    {{ ((contact.prenom || '?').charAt(0) + (contact.nom || '').charAt(0)).toUpperCase() }}
                                </div>
                                <div>
                                    <p class="text-sm font-bold text-gray-900">{{ contact.prenom }} {{ contact.nom }}</p>
                                    <p v-if="contact.poste" class="text-[10px] text-gray-400">{{ contact.poste }}</p>
                                </div>
                            </div>
                            <div class="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button @click="startEditContact(i, contact)" class="w-6 h-6 rounded-lg flex items-center justify-center text-gray-400 hover:text-orange-500 hover:bg-orange-50 transition-all">
                                    <i class="fas fa-pen text-[10px]"></i>
                                </button>
                                <button @click="deleteContact(contact, i)" class="w-6 h-6 rounded-lg flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all">
                                    <i class="fas fa-trash text-[10px]"></i>
                                </button>
                            </div>
                        </div>

                        <!-- Edition inline -->
                        <template v-if="editingContactIdx === i">
                            <div class="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-gray-200">
                                <input v-model="contactEditForm.prenom" placeholder="Prénom" class="border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-400 transition"/>
                                <input v-model="contactEditForm.nom" placeholder="Nom" class="border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-400 transition"/>
                                <input v-model="contactEditForm.email" placeholder="Email" class="border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-400 transition"/>
                                <input v-model="contactEditForm.telephone" placeholder="Téléphone" class="border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-400 transition"/>
                                <input v-model="contactEditForm.poste" placeholder="Poste" class="border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-400 transition"/>
                                <input v-model="contactEditForm.note" placeholder="Note" class="border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-400 transition"/>
                            </div>
                            <div class="flex gap-2 mt-2">
                                <button @click="saveContact(contact)" :disabled="loadingContactSave" class="flex items-center gap-1 px-3 py-1.5 bg-orange-500 text-white text-[10px] font-bold rounded-lg hover:bg-orange-600 transition-all disabled:opacity-60">
                                    <i v-if="loadingContactSave" class="fas fa-spinner fa-spin"></i>
                                    <i v-else class="fas fa-check"></i> Sauvegarder
                                </button>
                                <button @click="editingContactIdx = null" class="px-3 py-1.5 bg-gray-100 text-gray-600 text-[10px] font-bold rounded-lg hover:bg-gray-200 transition-all">Annuler</button>
                            </div>
                        </template>
                        <template v-else>
                            <div class="grid grid-cols-2 gap-1 text-xs text-gray-500 mt-1">
                                <span v-if="contact.email"><i class="fas fa-envelope text-[10px] mr-1 text-gray-300"></i>{{ contact.email }}</span>
                                <span v-if="contact.telephone"><i class="fas fa-phone text-[10px] mr-1 text-gray-300"></i>{{ contact.telephone }}</span>
                            </div>
                            <p v-if="contact.note" class="text-xs text-gray-400 mt-2 italic border-t border-gray-100 pt-2">{{ contact.note }}</p>
                        </template>
                    </div>

                    <!-- Add contact form -->
                    <div v-if="!showAddContact" class="text-center pt-2">
                        <button @click="showAddContact = true" class="flex items-center gap-2 text-xs font-bold text-orange-500 hover:text-orange-600 transition-colors mx-auto">
                            <i class="fas fa-plus text-[10px] bg-orange-100 rounded-full p-1"></i>
                            Ajouter un contact
                        </button>
                    </div>
                    <div v-else class="bg-orange-50 rounded-xl p-4 border border-orange-100">
                        <p class="text-[10px] font-bold text-orange-600 uppercase tracking-wide mb-3">Nouveau contact</p>
                        <div class="grid grid-cols-2 gap-2">
                            <input v-model="newContact.prenom" placeholder="Prénom" class="border border-gray-200 rounded-lg px-3 py-2 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-orange-400 transition"/>
                            <input v-model="newContact.nom" placeholder="Nom" class="border border-gray-200 rounded-lg px-3 py-2 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-orange-400 transition"/>
                            <input v-model="newContact.email" placeholder="Email" class="border border-gray-200 rounded-lg px-3 py-2 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-orange-400 transition"/>
                            <input v-model="newContact.telephone" placeholder="Téléphone" class="border border-gray-200 rounded-lg px-3 py-2 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-orange-400 transition"/>
                            <input v-model="newContact.poste" placeholder="Poste" class="border border-gray-200 rounded-lg px-3 py-2 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-orange-400 transition col-span-2"/>
                        </div>
                        <div class="flex gap-2 mt-3">
                            <button @click="addContact" :disabled="loadingContactSave" class="flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white text-xs font-bold rounded-xl hover:bg-orange-600 transition-all disabled:opacity-60">
                                <i v-if="loadingContactSave" class="fas fa-spinner fa-spin text-[10px]"></i>
                                <i v-else class="fas fa-plus text-[10px]"></i>
                                Ajouter
                            </button>
                            <button @click="showAddContact = false" class="px-4 py-2 bg-white border border-gray-200 text-gray-600 text-xs font-bold rounded-xl hover:bg-gray-50 transition-all">Annuler</button>
                        </div>
                    </div>

                    <div v-if="!detail.contacts || detail.contacts.length === 0" class="text-center py-8">
                        <i class="fas fa-users text-gray-200 text-3xl mb-2"></i>
                        <p class="text-xs text-gray-400">Aucun contact ajouté</p>
                    </div>
                </div>

                <!-- ── NOTES ── -->
                <div v-if="activeTab === 'notes'" class="flex flex-col h-full">
                    <div class="flex-1 overflow-y-auto p-6 space-y-3">
                        <div v-if="!detail.notes || detail.notes.length === 0" class="text-center py-10">
                            <i class="fas fa-sticky-note text-gray-200 text-3xl mb-2 block"></i>
                            <p class="text-xs text-gray-400">Aucune note pour ce prospect</p>
                        </div>
                        <div
                            v-for="note in detail.notes"
                            :key="note.id"
                            class="bg-yellow-50 border border-yellow-100 rounded-xl p-4 group relative">
                            <div class="flex items-start justify-between mb-2">
                                <div class="flex items-center gap-2">
                                    <div class="w-6 h-6 rounded-full bg-gradient-to-br from-orange-400 to-red-400 flex items-center justify-center text-white text-[9px] font-bold">
                                        {{ note.account ? (note.account.prenom.charAt(0) + note.account.nom.charAt(0)).toUpperCase() : 'U' }}
                                    </div>
                                    <div>
                                        <p class="text-[10px] font-bold text-gray-600">{{ note.account ? note.account.prenom + ' ' + note.account.nom : 'Moi' }}</p>
                                        <p class="text-[9px] text-gray-400">{{ note.created_at | momentDateTime }}</p>
                                    </div>
                                </div>
                                <button @click="deleteNote(note)" class="opacity-0 group-hover:opacity-100 w-6 h-6 rounded-lg flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all">
                                    <i class="fas fa-trash text-[10px]"></i>
                                </button>
                            </div>
                            <p class="text-sm text-gray-700 leading-relaxed">{{ note.content }}</p>
                        </div>
                    </div>

                    <!-- Add note -->
                    <div class="border-t border-gray-100 p-4 flex-shrink-0 bg-gray-50/50">
                        <div class="flex gap-2">
                            <textarea
                                v-model="newNote"
                                placeholder="Ajouter une note..."
                                rows="2"
                                @keydown.ctrl.enter="submitNote"
                                class="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition bg-white placeholder-gray-300">
                            </textarea>
                            <button
                                @click="submitNote"
                                :disabled="!newNote.trim() || loadingNote"
                                class="w-10 h-10 flex-shrink-0 self-end flex items-center justify-center bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                                <i v-if="loadingNote" class="fas fa-spinner fa-spin text-xs"></i>
                                <i v-else class="fas fa-paper-plane text-xs"></i>
                            </button>
                        </div>
                        <p class="text-[10px] text-gray-400 mt-1.5 ml-1">Ctrl+Entrée pour envoyer</p>
                    </div>
                </div>

                <!-- ── DOCUMENTS ── -->
                <div v-if="activeTab === 'documents'" class="p-6 space-y-4">
                    <!-- Upload zone -->
                    <div class="bg-gray-50 rounded-xl p-4 border border-gray-100">
                        <p class="text-[10px] font-bold text-gray-600 uppercase tracking-wide mb-3">Ajouter un document</p>
                        <input-file v-model="newDocUrl" key-name="prospect-doc" />
                        <button
                            @click="uploadDocument"
                            :disabled="!newDocUrl || loadingDoc"
                            class="mt-3 flex items-center gap-2 px-4 py-2 bg-orange-500 text-white text-xs font-bold rounded-xl hover:bg-orange-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                            <i v-if="loadingDoc" class="fas fa-spinner fa-spin text-[10px]"></i>
                            <i v-else class="fas fa-upload text-[10px]"></i>
                            Uploader
                        </button>
                    </div>

                    <!-- Documents list -->
                    <div v-if="detail.documents && detail.documents.length" class="space-y-2">
                        <div
                            v-for="doc in detail.documents"
                            :key="doc.id"
                            class="flex items-center gap-3 bg-white border border-gray-100 rounded-xl px-4 py-3 group hover:border-gray-200 transition-all">
                            <div class="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                                <i class="fas fa-file text-gray-400 text-sm"></i>
                            </div>
                            <div class="flex-1 min-w-0">
                                <p class="text-xs font-semibold text-gray-800 truncate">{{ doc.filename }}</p>
                                <p class="text-[10px] text-gray-400 mt-0.5">{{ doc.created_at | momentDate }} · {{ formatSize(doc.size) }}</p>
                            </div>
                            <div class="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button @click="deleteDocument(doc)" class="w-7 h-7 rounded-lg flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all">
                                    <i class="fas fa-trash text-xs"></i>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div v-else class="text-center py-8">
                        <i class="fas fa-folder-open text-gray-200 text-3xl mb-2 block"></i>
                        <p class="text-xs text-gray-400">Aucun document</p>
                    </div>
                </div>
            </div>

            <!-- Validate confirm -->
            <div v-if="showValidateConfirm" class="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-10 p-6">
                <div class="bg-white rounded-2xl p-6 shadow-2xl max-w-sm w-full text-center">
                    <div class="w-14 h-14 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-4">
                        <i class="fas fa-trophy text-green-500 text-2xl"></i>
                    </div>
                    <h3 class="font-black text-gray-900 text-lg mb-1">Valider ce prospect ?</h3>
                    <p class="text-sm text-gray-500 mb-6">Le prospect sera marqué comme <strong>gagné</strong> et converti en client.</p>
                    <div class="flex gap-3">
                        <button @click="showValidateConfirm = false" class="flex-1 py-2.5 bg-gray-100 text-gray-700 text-sm font-bold rounded-xl hover:bg-gray-200 transition-all">
                            Annuler
                        </button>
                        <button @click="validateProspect" :disabled="loadingValidate" class="flex-1 py-2.5 bg-green-500 text-white text-sm font-bold rounded-xl hover:bg-green-600 transition-all disabled:opacity-60">
                            <i v-if="loadingValidate" class="fas fa-spinner fa-spin text-xs mr-1"></i>
                            Confirmer
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment';

export default {
    name: 'ProspectDetailModal',
    props: {
        prospect: { required: true }
    },
    data() {
        return {
            activeTab: 'infos',
            loadingDetail: false,
            detail: { ...this.prospect, contacts: this.prospect.contacts || [], notes: [], documents: [] },

            editMode: false,
            editForm: {},
            loadingUpdate: false,

            editingContactIdx: null,
            contactEditForm: {},
            loadingContactSave: false,
            showAddContact: false,
            newContact: { prenom: '', nom: '', email: '', telephone: '', poste: '' },

            newNote: '',
            loadingNote: false,

            newDocUrl: '',
            loadingDoc: false,

            loadingValidate: false,
            showValidateConfirm: false,

            statusConfig: {
                nouveau:     { label: 'Nouveau' },
                contacte:    { label: 'Contacté' },
                en_cours:    { label: 'En cours' },
                proposition: { label: 'Proposition' },
                negociation: { label: 'Négociation' },
                relance:     { label: 'Relance' },
                gagne:       { label: 'Gagné' },
                perdu:       { label: 'Perdu' },
            },
        };
    },
    computed: {
        tabs() {
            return [
                { key: 'infos',     label: 'Infos',     icon: 'fas fa-info-circle' },
                { key: 'contacts',  label: 'Contacts',  icon: 'fas fa-users', count: this.detail.contacts ? this.detail.contacts.length : 0 },
                { key: 'notes',     label: 'Notes',     icon: 'fas fa-sticky-note', count: this.detail.notes ? this.detail.notes.length : 0 },
                { key: 'documents', label: 'Documents', icon: 'fas fa-file', count: this.detail.documents ? this.detail.documents.length : 0 },
            ];
        }
    },
    filters: {
        momentDate(v) { return v ? moment(v).format('DD/MM/YYYY') : '—'; },
        momentDateTime(v) { return v ? moment(v).format('DD/MM HH:mm') : '—'; },
    },
    methods: {
        getStatusBadge(status) {
            const map = {
                nouveau:     'bg-gray-100 text-gray-600',
                contacte:    'bg-blue-100 text-blue-700',
                en_cours:    'bg-yellow-100 text-yellow-700',
                proposition: 'bg-orange-100 text-orange-700',
                negociation: 'bg-purple-100 text-purple-700',
                relance:     'bg-red-100 text-red-700',
                gagne:       'bg-green-100 text-green-700',
                perdu:       'bg-slate-100 text-slate-600',
            };
            return map[status] || 'bg-gray-100 text-gray-600';
        },
        formatSize(bytes) {
            if (!bytes) return '—';
            if (bytes < 1024) return bytes + ' B';
            if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
            return (bytes / 1048576).toFixed(1) + ' MB';
        },

        // Load full detail
        async loadDetail() {
            this.loadingDetail = true;
            const res = await this.$request.get('/commercial/prospects/' + this.prospect.id);
            if (res && res.prospect) {
                this.detail = res.prospect;
                this.editForm = { company_name: res.prospect.company_name, status: res.prospect.status, effectif: res.prospect.effectif };
            }
            this.loadingDetail = false;
        },

        // Save infos
        async saveInfos() {
            this.loadingUpdate = true;
            const res = await this.$request.post('/commercial/prospects/' + this.detail.id, this.editForm);
            this.loadingUpdate = false;
            if (res && res.prospect) {
                Object.assign(this.detail, res.prospect);
                this.editMode = false;
                this.$emit('updated', res.prospect);
            }
        },

        // Contacts
        startEditContact(i, contact) {
            this.editingContactIdx = i;
            this.contactEditForm = { ...contact };
        },
        async saveContact(contact) {
            this.loadingContactSave = true;
            const res = await this.$request.post(`/commercial/prospects/${this.detail.id}/contacts/${contact.id}`, this.contactEditForm);
            this.loadingContactSave = false;
            if (res && res.contact) {
                Object.assign(contact, res.contact);
                this.editingContactIdx = null;
            }
        },
        async addContact() {
            this.loadingContactSave = true;
            const res = await this.$request.post(`/commercial/prospects/${this.detail.id}/contacts`, this.newContact);
            this.loadingContactSave = false;
            if (res && res.contact) {
                this.detail.contacts.push(res.contact);
                this.newContact = { prenom: '', nom: '', email: '', telephone: '', poste: '' };
                this.showAddContact = false;
            }
        },
        async deleteContact(contact, i) {
            if (!confirm('Supprimer ce contact ?')) return;
            await this.$request.post(`/commercial/prospects/${this.detail.id}/contacts/${contact.id}/delete`, {});
            this.detail.contacts.splice(i, 1);
        },

        // Notes
        async submitNote() {
            if (!this.newNote.trim()) return;
            this.loadingNote = true;
            const res = await this.$request.post(`/commercial/prospects/${this.detail.id}/notes`, { content: this.newNote });
            this.loadingNote = false;
            if (res && res.note) {
                if (!this.detail.notes) this.$set(this.detail, 'notes', []);
                this.detail.notes.unshift(res.note);
                this.newNote = '';
            }
        },
        async deleteNote(note) {
            if (!confirm('Supprimer cette note ?')) return;
            await this.$request.post(`/commercial/prospects/${this.detail.id}/notes/${note.id}/delete`, {});
            const i = this.detail.notes.findIndex(n => n.id === note.id);
            if (i !== -1) this.detail.notes.splice(i, 1);
        },

        // Documents
        async uploadDocument() {
            if (!this.newDocUrl) return;
            this.loadingDoc = true;
            const res = await this.$request.post(`/commercial/prospects/${this.detail.id}/documents`, { url: this.newDocUrl });
            this.loadingDoc = false;
            if (res && res.document) {
                if (!this.detail.documents) this.$set(this.detail, 'documents', []);
                this.detail.documents.unshift(res.document);
                this.newDocUrl = '';
            }
        },
        async deleteDocument(doc) {
            if (!confirm('Supprimer ce document ?')) return;
            await this.$request.post(`/commercial/prospects/${this.detail.id}/documents/${doc.id}/delete`, {});
            const i = this.detail.documents.findIndex(d => d.id === doc.id);
            if (i !== -1) this.detail.documents.splice(i, 1);
        },

        // Validate
        confirmValidate() { this.showValidateConfirm = true; },
        async validateProspect() {
            this.loadingValidate = true;
            const res = await this.$request.post(`/commercial/prospects/${this.detail.id}/validate`, {});
            this.loadingValidate = false;
            if (res) {
                this.showValidateConfirm = false;
                this.detail.validated_at = new Date().toISOString();
                this.$emit('validated', this.detail);
            }
        },
    },
    mounted() {
        this.editForm = { company_name: this.prospect.company_name, status: this.prospect.status, effectif: this.prospect.effectif };
        this.loadDetail();
    }
};
</script>